import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { trackEvent } from "@/lib/analytics";
import { CheckCircle2, Loader2, XCircle, ShoppingBag } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          clear();
          trackEvent("purchase_completed", "purchase", {
            transaction_id: sessionId,
            value: res.data.amount != null ? Number(res.data.amount) : undefined,
            currency: (res.data.currency || "EUR").toUpperCase(),
          });
          return;
        }
        if (res.data.status === "expired" || res.data.status === "failed") { setStatus("error"); return; }
      } catch (e) { console.debug("Payment status poll retry:", e?.message || e); }
      if (attempts >= 10) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafafa] p-6">
      <div className="bg-white rounded-3xl border border-zinc-100 p-10 max-w-md w-full text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="payment-result">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto text-zinc-400 animate-spin" />
            <h1 className="font-heading font-medium text-2xl mt-6">Confirming payment…</h1>
            <p className="text-zinc-500 mt-2 font-body">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
            <h1 className="font-heading font-medium text-2xl mt-6" data-testid="payment-success-title">Payment successful</h1>
            <p className="text-zinc-500 mt-2 font-body">Thank you for your order. A confirmation has been recorded.</p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-black text-white rounded-full px-6 py-3 font-body font-semibold" data-testid="back-home-button">
              <ShoppingBag size={16} /> Continue shopping
            </Link>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle size={48} className="mx-auto text-rose-500" />
            <h1 className="font-heading font-medium text-2xl mt-6">Payment not confirmed</h1>
            <p className="text-zinc-500 mt-2 font-body">
              {status === "timeout" ? "This is taking longer than expected." : "Something went wrong with your payment."}
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-black text-white rounded-full px-6 py-3 font-body font-semibold">Back to StoreIG</Link>
          </>
        )}
      </div>
    </div>
  );
}
